import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  ApiErrorShape,
  CareerRoleActivationStatus,
  CareerRolePopulationRunResponse,
  SetCareerRoleActivationResponse
} from '../../../../models';
import { ActivationStatusWatcherService } from './activation-status-watcher.service';
import { BackgroundJobTrackerService } from './background-job-tracker.service';

@Injectable({ providedIn: 'root' })
export class CareerRoleAdminActionsService {
  private readonly api = inject(CsrWebApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly tracker = inject(BackgroundJobTrackerService);
  private readonly activationWatcher = inject(ActivationStatusWatcherService);

  async setRoleActivation(
    roleSlug: string,
    active: boolean,
    force = false,
    onPromoted?: () => void
  ): Promise<SetCareerRoleActivationResponse> {
    const response = await firstValueFrom(this.api.setCareerRoleActivation(roleSlug, active, force));
    const status = this.resolveActivationStatus(response);
    this.showActivationFeedback(response, () => this.activationWatcher.watchRole(roleSlug, status, onPromoted));
    return response;
  }

  async setSpecializationActivation(
    roleSlug: string,
    specializationSlug: string,
    active: boolean,
    force = false,
    onPromoted?: () => void
  ): Promise<SetCareerRoleActivationResponse> {
    const response = await firstValueFrom(
      this.api.setCareerRoleSpecializationActivation(roleSlug, specializationSlug, active, force)
    );
    const status = this.resolveActivationStatus(response);
    this.showActivationFeedback(response, () =>
      this.activationWatcher.watchSpecialization(roleSlug, specializationSlug, status, onPromoted)
    );
    return response;
  }

  async setSeniorityActivation(roleSlug: string, seniority: string, active: boolean): Promise<SetCareerRoleActivationResponse> {
    const response = await firstValueFrom(
      this.api.setCareerRoleSeniorityLevelActivation(roleSlug, seniority, active)
    );
    this.showActivationFeedback(response);
    return response;
  }

  async populateSkills(roleSlug: string, onComplete?: () => void): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.populateSkills({
        careerRoleSlugs: [roleSlug],
        locations: ['India', 'US', 'Remote'],
        forceRefresh: false,
        updateExistingJobAspects: true,
        maxAiTokensPerRun: null,
        maxAiCostUsdPerRun: null,
        isCorrectionRun: false
      })
    );
    this.trackRun(response, `Populate skills: ${roleSlug}`, roleSlug, onComplete);
    return response;
  }

  async populateResources(roleSlug: string, onComplete?: () => void): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.populateResources({
        careerRoleSlugs: [roleSlug],
        providers: ['youtube', 'github', 'mdn', 'microsoft_learn'],
        maxQueriesPerRole: 50,
        maxResourcesPerQuery: 8,
        maxEstimatedHours: 0.5,
        recentPublishedWithinDays: 90,
        useAiEnrichment: true,
        isCorrectionRun: false,
        skipRecentlyIngested: true,
        ingestionGracePeriodDays: 7,
        publishHighConfidence: true,
        publishThreshold: 0.45,
        autoEnrich: false
      })
    );
    this.trackRun(response, `Populate resources: ${roleSlug}`, roleSlug, onComplete);
    return response;
  }

  async populateSkillResources(
    roleSlug: string,
    skillId: string,
    topics: string[],
    onComplete?: () => void
  ): Promise<CareerRolePopulationRunResponse | null> {
    const trimmed = topics.map((t) => t.trim()).filter((t) => t.length > 0);
    if (!trimmed.length) {
      this.snackBar.open('No topic facets on this skill — add topics first.', 'Close', { duration: 8000 });
      return null;
    }
    const response = await firstValueFrom(
      this.api.populateSkillResources({
        skillId,
        topics: trimmed,
        careerRoleSlugs: [roleSlug],
        providers: [],
        maxResourcesPerQuery: 8,
        maxEstimatedHours: 2.5,
        recentPublishedWithinDays: 90,
        skipRecentlyIngested: true,
        ingestionGracePeriodDays: 30,
        autoEnrich: false,
        useAiEnrichment: true,
        isCorrectionRun: false,
        publishThreshold: 0.45
      })
    );
    const label =
      trimmed.length === 1
        ? `Populate skill resources: ${trimmed[0]}`
        : `Populate skill resources (${trimmed.length} topics)`;
    this.trackRun(response, label, roleSlug, onComplete);
    return response;
  }

  async populateSkillResourcesGapFill(
    roleSlug: string,
    skillId: string,
    topicName: string,
    publishThreshold: number,
    onComplete?: () => void
  ): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.populateSkillResources({
        skillId,
        topics: [topicName.trim()],
        careerRoleSlugs: [roleSlug],
        providers: [],
        maxResourcesPerQuery: 8,
        maxEstimatedHours: 2.5,
        recentPublishedWithinDays: 90,
        skipRecentlyIngested: false,
        ingestionGracePeriodDays: 30,
        autoEnrich: false,
        useAiEnrichment: true,
        isCorrectionRun: false,
        publishThreshold,
        useProviderCatalog: true
      })
    );
    this.trackRun(response, `Gap fill: ${topicName}`, roleSlug, onComplete);
    return response;
  }

  async refreshJobAspects(roleSlug: string, onComplete?: () => void): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.refreshJobAspects({
        careerRoleSlugs: [roleSlug],
        locations: ['India', 'US', 'Remote'],
        fullRefreshAllRoles: false,
        isCorrectionRun: false
      })
    );
    this.trackRun(response, `Refresh aspects: ${roleSlug}`, roleSlug, onComplete);
    return response;
  }

  async rebalanceTaxonomy(roleSlug: string, onComplete?: () => void): Promise<void> {
    const response = await firstValueFrom(
      this.api.rebalanceSkillTaxonomy({
        careerRoleSlugs: [roleSlug],
        dryRun: false,
        createGeneralistSpec: true,
        repopulateAfterRebalance: true
      })
    );
    if (response.repopulateRunId) {
      this.tracker.track(
        {
          runId: response.repopulateRunId,
          label: `Rebalance taxonomy: ${roleSlug}`,
          roleSlug,
          operationType: 'rebalance_role_skill_taxonomy',
          onComplete
        }
      );
    } else {
      this.snackBar.open('Rebalance queued', 'Close', { duration: 2500 });
    }
  }

  async batchGenerateLessons(
    roleSlug: string,
    seniorityLevel: string,
    specializationSlug?: string | null,
    onComplete?: () => void,
    forceRegenerate = false
  ): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.batchGenerateCanonicalLessons({
        roleSlug,
        seniorityLevel,
        specializationSlug: specializationSlug || undefined,
        publishImmediately: true,
        forceRegenerate
      })
    );
    const scope = specializationSlug
      ? `${seniorityLevel} / ${specializationSlug} (focus)`
      : `${seniorityLevel} (core)`;
    const mode = forceRegenerate ? 'force regenerate' : 'skip existing';
    this.trackRun(response, `Batch lessons: ${scope} [${mode}]`, roleSlug, onComplete);
    return response;
  }

  async forceGenerateAllLessons(
    roleSlug: string,
    seniorityLevel: string,
    specializationSlug?: string | null,
    onComplete?: () => void
  ): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.forceGenerateAllCanonicalLessons({
        roleSlug,
        seniorityLevel,
        specializationSlug: specializationSlug || undefined,
        publishImmediately: true
      })
    );
    const scope = specializationSlug
      ? `${seniorityLevel} / ${specializationSlug} (focus)`
      : `${seniorityLevel} (core)`;
    this.trackRun(response, `Force generate all lessons: ${scope}`, roleSlug, onComplete);
    return response;
  }

  async batchGenerateFullyAiLessons(
    roleSlug: string,
    forceRegenerate: boolean,
    onComplete?: () => void
  ): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.batchGenerateFullyAiCanonicalLessons({
        roleSlug,
        forceRegenerate
      })
    );
    const mode = forceRegenerate ? 'force regenerate' : 'skip existing';
    this.trackRun(response, `Fully AI lessons (role) [${mode}]`, roleSlug, onComplete);
    return response;
  }

  async backfillLessonTargetProficiency(
    roleSlug: string,
    onComplete?: () => void
  ): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(
      this.api.backfillCanonicalLessonTargetProficiency({
        roleSlug,
        forceOverwrite: false,
        chunkSize: 500,
        aiBatchSize: 10
      })
    );
    this.trackRun(response, `Backfill lesson proficiency (AI)`, roleSlug, onComplete);
    return response;
  }

  async createRoleWithAi(name: string): Promise<CareerRolePopulationRunResponse> {
    const response = await firstValueFrom(this.api.createCareerRoleWithAi({ name: name.trim() }));
    this.trackRun(response, `Create role: ${name.trim()}`);
    return response;
  }

  async addSpecialization(roleSlug: string, name: string, slug?: string): Promise<void> {
    await firstValueFrom(
      this.api.addSpecialization(roleSlug, {
        name: name.trim(),
        slug: slug?.trim() || null,
        inheritFromLowerSeniority: true,
        overwriteExistingSkills: false,
        maxSkillsPerSeniority: 8
      })
    );
    this.snackBar.open('Specialization added', 'Close', { duration: 2500 });
  }

  async removeSpecialization(roleSlug: string, specializationSlug: string): Promise<void> {
    await firstValueFrom(this.api.removeSpecialization(roleSlug, specializationSlug));
    this.snackBar.open('Specialization removed', 'Close', { duration: 2500 });
  }

  trackExternalRun(
    run: CareerRolePopulationRunResponse,
    label: string,
    roleSlug?: string,
    onComplete?: () => void
  ): void {
    this.trackRun(run, label, roleSlug, onComplete);
  }

  errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiErrorShape;
      if (body?.errors) {
        const first = Object.values(body.errors)[0];
        if (typeof first === 'string' && first.length > 0) {
          return first;
        }
      }
      return body?.title ?? `Request failed (${error.status})`;
    }
    return 'Unexpected error';
  }

  private trackRun(
    run: CareerRolePopulationRunResponse,
    label: string,
    roleSlug?: string,
    onComplete?: () => void
  ): void {
    if (!run?.id) {
      return;
    }
    this.tracker.track(
      {
        runId: run.id,
        label,
        roleSlug,
        operationType: run.operationType,
        onComplete
      },
      run
    );
  }

  private resolveActivationStatus(response: SetCareerRoleActivationResponse): CareerRoleActivationStatus {
    return response.status ?? (response.active ? 'active' : 'inactive');
  }

  private showActivationFeedback(response: SetCareerRoleActivationResponse, onActivating?: () => void): void {
    const status = this.resolveActivationStatus(response);
    if (status === 'activating') {
      onActivating?.();
      this.snackBar.open('Population queued — status: Activating', 'Close', { duration: 4000 });
      return;
    }
    if (response.active && response.meetsCoverageTarget === false) {
      this.snackBar.open('Activated manually — verified coverage is not yet sufficient.', 'Close', { duration: 4500 });
      return;
    }
    this.snackBar.open(`Status updated: ${status}`, 'Close', { duration: 2500 });
  }
}